import type { AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, effect, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Tooltip } from 'bootstrap';
import { StringConstants } from './constants/string-constants';
import type { User } from './models/user';
import { DateSelectedService } from './rcr-exchange/shared/date-selected.service';
import { AuthenticateService } from './shared/services/authenticate.service';
import { FmLoadingPageService, LoadingSingleton } from './shared/services/fm-loading-page.service';
import { HealthCheckService } from './shared/services/health-check.service';
import { SessionService } from './shared/services/session.service';
import { UserService } from './shared/services/user.service';
import { MenuConstants } from './constants/menu-constants';

@Component({
  standalone: false,
  selector: 'invd-app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  public menu: Array<object> = [];
  public displayName = { firstName: '', lastName: '' };
  public logoutLink: Array<object> = new Array<object>();
  public readonly currentYear = new Date().getFullYear();

  public showSessionExtendPopup$ = false;
  public timeLeft = 180;
  public healthCheckIntervalTime = 0;
  public interval: ReturnType<typeof setInterval> | undefined;
  public healthCheckInterval: ReturnType<typeof setInterval> | undefined;
  public screenIdleTime = 0;
  public countOfExtension = 0;

  public fmLoadingPageService = inject(FmLoadingPageService);
  private userService = inject(UserService);
  private authenticationService = inject(AuthenticateService);
  private sessionService = inject(SessionService);
  private healthCheckService = inject(HealthCheckService);
  private router = inject(Router);
  private dateSelectedService = inject(DateSelectedService);
  private _loadingSingleton = inject(LoadingSingleton);
  private cdr = inject(ChangeDetectorRef);

  private readonly reportsMenuTitle = MenuConstants.REPORTS;
  private readonly validateArmPoolMenu = {
    title: MenuConstants.VALIDATE_ARM_POOL_TITLE,
    url: MenuConstants.VALIDATE_ARM_POOL_PATH,
  };
  private readonly jumegaReportMenu = {
    title: MenuConstants.JUMEGA_REPORT_TITLE,
    url: MenuConstants.JUMEGA_REPORT_PATH,
  };

  constructor() {
    effect(() => {
      this.fmLoadingPageService.isLoaderRequesting();
      this.cdr.markForCheck();
    });
  }

  ngOnInit() {
    this.screenIdleTime = StringConstants.HEALTH_CHECK_SCREEN_IDLE_TIME;
    this.healthCheckIntervalTime = StringConstants.HEALTH_CHECK_POLL_INTERVAL * 60 * 1000;
    if (!this.sessionService.getSessionTime()) {
      this.sessionService.setSessionTime(new Date());
    }
    this.subscribeToAWSUserProfileUpdates();
    this.userService.buildAWSUserProfile();
  }

  ngAfterViewInit() {
    this.healthCheck(this.healthCheckIntervalTime);
    this.initializeTooltips();
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }

  private initializeTooltips(): void {
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
    tooltipTriggerList.forEach((tooltipTriggerEl) => {
      new Tooltip(tooltipTriggerEl, {
        customClass: 'fm-tooltip',
      });
    });
  }

  ngOnDestroy() {
    this.sessionService.clearSession();
    clearInterval(this.interval);
    clearInterval(this.healthCheckInterval);
  }

  private subscribeToAWSUserProfileUpdates(): void {
    this.userService.userAWSProfileSubscription$.subscribe({
      next: (data: any) => {
        this.pushLogoutLink();
        this.subscribeToUserProfileUpdates();
        this.authenticationService.authenticate(data);
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        console.error(error);
        this.sessionService.frameLogoutURLlink();
        this.cdr.markForCheck();
      },
    });
  }

  private subscribeToUserProfileUpdates(): void {
    this.userService.userProfileSubscription$.subscribe({
      next: (user: User) => {
        this.displayName = {
          firstName: user.firstName,
          lastName: user.lastName,
        };

        this.menu = this.withValidateArmPoolMenu(user.userMenu);

        const url = this.userService.isCEUser
          ? MenuConstants.REMIC_FILE_UPLOAD_VALIDATION_PATH
          : this.userService.isCMOShakeoutUser
            ? MenuConstants.CMO_REMIC_HOME_PATH
            : this.router.url;

        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
          this.router.navigate([`/${url}`]);
          this.cdr.markForCheck();
        });
      },
      error: (error: any) => {
        console.error(error);
        this.cdr.markForCheck();
      },
    });
  }

  public pushLogoutLink(): void {
    this.logoutLink = [
      {
        title: 'Logout',
        url: this.sessionService.frameLogoutURLlink(),
        action: () => this.closeAndLogOut(),
      },
    ];
  }

  public exitBannerIconClick(exitBannerIconObj: any): void {
    if (exitBannerIconObj.showNavigationPopup === true) {
      this.dateSelectedService.broadcastExitIconClickInEnterBalances(
        exitBannerIconObj.showNavigationPopup.toString(),
      );
      this.dateSelectedService.broadcastExitIconClickInReviewAndCommit(
        exitBannerIconObj.showNavigationPopup.toString(),
      );
    }
  }

  //code for session timeout

  public healthCheck(intervalTime: number) {
    this.healthCheckInterval = setInterval(() => {
      const idleLimit = StringConstants.HEALTH_CHECK_SCREEN_IDLE_TIME * 60 * 1000;
      const logoutWindow = StringConstants.HEALTH_CHECK_LOGOUT_TIMER * 1000;
      const screenIdleTime = new Date().getTime() - this.sessionService.getSessionTime();

      if (screenIdleTime >= idleLimit + logoutWindow) {
        this.closeAndLogOut();
        return;
      }

      if (screenIdleTime >= idleLimit && !this.showSessionExtendPopup$) {
        this.timeLeft = StringConstants.HEALTH_CHECK_LOGOUT_TIMER;
        this.startTimer();
        this.showSessionExtendPopup$ = true;
        this.cdr.detectChanges();
      }
    }, intervalTime);
  }

  public startTimer(): void {
    clearInterval(this.interval);
    const logoutDeadline = Date.now() + this.timeLeft * 1000;

    this.interval = setInterval(() => {
      this.timeLeft = Math.max(0, Math.ceil((logoutDeadline - Date.now()) / 1000));

      if (this.timeLeft > 0) {
        this.cdr.detectChanges();
      } else {
        this.closeAndLogOut();
      }
    }, 1000);
  }

  public callHealthCheckService(): void {
    this.healthCheckService.callHealthCheck().subscribe({
      next: () => {
        this.extendSession();
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error(error);
        this.cdr.markForCheck();
      },
    });
  }

  private extendSession(): void {
    this.showSessionExtendPopup$ = false;

    clearInterval(this.interval);
    clearInterval(this.healthCheckInterval);

    this.healthCheckIntervalTime = StringConstants.HEALTH_CHECK_POLL_INTERVAL * 60 * 1000;
    this.healthCheck(this.healthCheckIntervalTime);
  }

  public closeAndLogOut(): void {
    clearInterval(this.interval);
    clearInterval(this.healthCheckInterval);
    this.showSessionExtendPopup$ = false;
    this.sessionService.clearSession();
    window.location.replace(this.sessionService.frameLogoutURLlink());
  }

  private withValidateArmPoolMenu(menu: Array<object>): Array<object> {
    const reportsMenu = menu.find(
      (item) => (item as { title?: unknown }).title === this.reportsMenuTitle,
    ) as { list?: Array<Record<string, unknown>> } | undefined;
    if (!reportsMenu || !Array.isArray(reportsMenu.list)) {
      return menu;
    }

    const hasValidateArmPool = reportsMenu.list.some(
      (entry) =>
        (entry as { title?: unknown }).title === this.validateArmPoolMenu.title ||
        (entry as { url?: unknown }).url === this.validateArmPoolMenu.url,
    );
    const hasJumegaReport = reportsMenu.list.some(
      (entry) =>
        (entry as { title?: unknown }).title === this.jumegaReportMenu.title ||
        (entry as { url?: unknown }).url === this.jumegaReportMenu.url,
    );

    if (!hasValidateArmPool) {
      reportsMenu.list.push({ ...this.validateArmPoolMenu });
    }
    if (!hasJumegaReport) {
      reportsMenu.list.push({ ...this.jumegaReportMenu });
    }

    return menu;
  }
}
